<template>
  <!-- <div class="flex pb-6 justify-between gap-4">
    <div class="flex gap-4">
      <ExportFetch
        exportEndpoint="/liquidation/user/export"
        :fileName="$t('liquidation.title').toLowerCase()"
        @export-started="handleExportStarted"
        @export-completed="handleExportCompleted"
        @export-failed="handleExportFailed"
      />

      <PrintTableComponent
        :title="$t('liquidation.title')"
        :fields="fields"
        :items="items"
        @print-started="handlePrintStarted"
        @print-completed="handlePrintCompleted"
        @print-failed="handlePrintFailed"
      />
    </div>
  </div> -->

  <TitlePrimary :title="$t('liquidation.title')" class="w-full" />
  <div class="flex flex-wrap items-center gap-3 py-4">
    <label for="contract-property-type">{{ $t('liquidation.propertyType') }}</label>
    <select id="contract-property-type" v-model="propertyType" :disabled="uploading" class="border rounded-lg p-2">
      <option value="comunidad">{{ $t('menu.farms') }}</option>
      <option value="inmueble">{{ $t('menu.estates') }}</option>
    </select>
    <p class="w-full text-sm text-gray-600">{{ $t('liquidation.propertyTypeHelp') }}</p>
    <RouterLink to="/add-light"><ButtonWhite>{{ $t('supplyLight.buttons.addNew') }} · {{ $t('menu.electricity') }}</ButtonWhite></RouterLink>
    <RouterLink to="/add-gas"><ButtonWhite>{{ $t('supplyGas.buttons.addNew') }} · {{ $t('menu.gas') }}</ButtonWhite></RouterLink>
  </div>

  <div class="pt-4 flex flex-row gap-4 justify-center pb-4">
    <div class="pt-4 flex flex-col lg:flex-row gap-4 px-2">
      <!-- Sección de archivos -->
      <div class="p-6 md:p-8 py-8 border rounded-xl text-center w-full lg:w-2/5 flex flex-col gap-3 md:gap-4 shadow-card">
        <h3 class="text-lg md:text-xl font-semibold">{{ $t('liquidation.upload.title') }}</h3>
        <!-- description updates based on selection -->
        <p class="text-gray-600 text-sm md:text-base">
          <template v-if="selectedFile">
            {{ selectedFile.name }}
          </template>
          <template v-else>
            {{ $t('liquidation.upload.description') }}
          </template>
        </p>

        <!-- hidden file input -->
        <input
          type="file"
          ref="fileInput"
          class="hidden"
          accept=".xlsx,.csv,.xls"
          @change="handleFileChange"
        />

        <ButtonWhite class="mt-2 md:mt-4 mx-auto whitespace-normal break-words text-center" @click="triggerFileUpload">
          {{ $t('liquidation.upload.button') }}
        </ButtonWhite>

        <p
          v-if="uploadMessage"
          class="mt-2 text-xs md:text-sm"
          :class="uploadMessageClasses[uploadMessageType]"
        >
          {{ uploadMessage }}
        </p>
      </div>

      <!-- Sección de acciones -->
      <div class="flex flex-col gap-2 w-full lg:w-3/5">
        <!-- Fila superior -->
        <div class="grid grid-cols-1 gap-2">
          <div
            class="p-4 py-8 md:py-10 border rounded-xl flex flex-col items-center justify-center gap-2 shadow-card cursor-pointer"
            :class="{ 'opacity-50 pointer-events-none': uploading }"
            @click="uploadContracts"
          >
            <span class="text-sm md:text-base">
              <template v-if="uploading">
                {{ $t('liquidation.uploading') }}
              </template>
              <template v-else>
                {{ $t('liquidation.actions.update') }}
              </template>
            </span>
            <ArrowReload :class="['w-6 h-6 md:w-8 md:h-8', uploading ? 'animate-spin' : '']" />
          </div>

          <!-- <div class="p-4 py-8 md:py-10 border rounded-xl flex flex-col items-center justify-center gap-2 shadow-card cursor-pointer">
            <span class="text-sm md:text-base">{{ $t('liquidation.actions.validate') }}</span>
            <check class="w-6 h-6 md:w-8 md:h-8" />
          </div> -->
        </div>

        <!-- Checkbox -->
        <div class="p-3 md:p-4 border rounded-xl flex items-center gap-2 shadow-card">
          <input type="checkbox" id="cbx" class="hidden" v-model="updateIfExists">
          <label for="cbx" class="check cursor-pointer">
            <svg class="w-5 h-5 md:w-6 md:h-6" viewBox="0 0 18 18">
              <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
              <polyline points="1 9 7 14 15 4"></polyline>
            </svg>
          </label>
          <span class="text-sm md:text-base">{{ $t('liquidation.actions.checkbox') }}</span>
        </div>
      </div>
    </div>
  </div>

  <!-- Resultado de la carga (ADS-0085): totales, aviso cuando no se actualiza nada y detalle por CUPS,
       con la paleta de la app (cian #2AB4D9, texto #385660, estados #28a745 / #7CADB9 / #fd7e14 / #dc3545). -->
  <section v-if="uploadSummary" class="mt-6 space-y-6">
    <div class="bg-white rounded-xl border border-gray-200 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.1)] overflow-hidden">
      <div class="px-6 py-3 bg-[#2AB4D9] text-white font-semibold text-sm md:text-base uppercase tracking-wider">
        {{ $t('liquidation.result.title') }}
      </div>
      <div class="p-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div
          v-for="tile in summaryTiles"
          :key="tile.key"
          class="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm"
        >
          <div class="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-500">
            <span class="w-3 h-3 rounded-full shrink-0" :class="tile.dot"></span>
            <span>{{ tile.label }}</span>
          </div>
          <p class="mt-2 text-3xl font-semibold" :class="tile.text">{{ tile.value }}</p>
        </div>
      </div>
      <p
        v-if="summaryNotice"
        class="mx-6 mb-6 rounded-lg border px-4 py-3 text-sm text-[#385660]"
        :class="summaryNoticeClasses[summaryNotice.type]"
      >
        {{ summaryNotice.text }}
      </p>
    </div>

    <!-- Actualizados y cargados -->
    <div v-if="updatedRows.length" class="bg-white rounded-xl border border-gray-200 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.1)] overflow-hidden">
      <div class="px-6 py-3 border-b border-gray-200 bg-[#f0f9fb] flex items-center gap-2">
        <span class="w-3 h-3 rounded-full bg-[#28a745]"></span>
        <h4 class="font-semibold text-sm md:text-base text-[#385660]">
          {{ $t('liquidation.updated_records.title', { count: updatedRows.length }) }}
        </h4>
      </div>
      <div class="overflow-x-auto">
        <table class="min-w-full text-left">
          <thead>
            <tr class="text-xs font-medium uppercase tracking-wider text-[#42A9C2]">
              <th class="px-6 py-3 whitespace-nowrap">{{ $t('liquidation.updated_records.excel_line') }}</th>
              <th class="px-6 py-3">{{ $t('liquidation.updated_records.cups') }}</th>
              <th class="px-6 py-3">{{ $t('liquidation.updated_records.result') }}</th>
            </tr>
          </thead>
          <tbody class="text-sm text-gray-600">
            <tr
              v-for="(row, index) in normalizedUpdatedRows"
              :key="`${row.cups || 'row'}-${index}`"
              class="border-t border-gray-100 align-top hover:bg-gray-50"
            >
              <td class="px-6 py-3 whitespace-nowrap text-gray-500">{{ row.excelLine ?? '-' }}</td>
              <td class="px-6 py-3 whitespace-nowrap font-medium text-[#385660]">{{ row.cups || '-' }}</td>
              <td class="px-6 py-3">
                <p class="font-semibold text-[#28a745]">{{ row.result }}</p>
                <ul v-if="row.changes.length" class="mt-2 flex flex-wrap gap-2">
                  <li
                    v-for="change in row.changes"
                    :key="change.field"
                    class="rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-600"
                  >
                    <span class="font-medium text-[#385660]">{{ change.label }}:</span>
                    {{ change.from }} → {{ change.to }}
                  </li>
                </ul>
                <p v-if="row.warnings.length" class="mt-2 text-xs text-[#fd7e14]">{{ row.warnings.join(' ') }}</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- No actualizados -->
    <div v-if="notUpdatedFields.length" class="bg-white rounded-xl border border-gray-200 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.1)] overflow-hidden">
      <div class="px-6 py-3 border-b border-gray-200 bg-[#f0f9fb] flex items-center gap-2">
        <span class="w-3 h-3 rounded-full bg-[#dc3545]"></span>
        <h4 class="font-semibold text-sm md:text-base text-[#385660]">
          {{ $t('liquidation.not_updated_records.title', { count: notUpdatedFields.length }) }}
        </h4>
      </div>
      <div class="overflow-x-auto">
        <table class="min-w-full text-left">
          <thead>
            <tr class="text-xs font-medium uppercase tracking-wider text-[#42A9C2]">
              <th class="px-6 py-3 whitespace-nowrap">{{ $t('liquidation.not_updated_records.excel_line') }}</th>
              <th class="px-6 py-3">{{ $t('liquidation.not_updated_records.cups') }}</th>
              <th class="px-6 py-3">{{ $t('liquidation.not_updated_records.reason') }}</th>
            </tr>
          </thead>
          <tbody class="text-sm text-gray-600">
            <tr
              v-for="(log, index) in normalizedNotUpdatedFields"
              :key="`${log.cups}-${log.excel_line}-${index}`"
              class="border-t border-gray-100 align-top hover:bg-gray-50"
            >
              <td class="px-6 py-3 whitespace-nowrap text-gray-500">{{ log.excel_line ?? '-' }}</td>
              <td class="px-6 py-3 whitespace-nowrap font-medium text-[#385660]">{{ log.cups || '-' }}</td>
              <td class="px-6 py-3 text-[#dc3545]">
                <ul v-if="log.reasons.length > 1" class="list-disc pl-4 space-y-1">
                  <li v-for="(reason, position) in log.reasons" :key="position">{{ reason }}</li>
                </ul>
                <span v-else>{{ log.reasons[0] || '-' }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Sin cambios -->
    <div v-if="alreadyUpdatedRows.length" class="bg-white rounded-xl border border-gray-200 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.1)] overflow-hidden">
      <div class="px-6 py-3 border-b border-gray-200 bg-[#f0f9fb] flex items-center gap-2">
        <span class="w-3 h-3 rounded-full bg-[#7CADB9]"></span>
        <h4 class="font-semibold text-sm md:text-base text-[#385660]">
          {{ $t('liquidation.unchanged_records.title', { count: alreadyUpdatedRows.length }) }}
        </h4>
      </div>
      <div class="overflow-x-auto">
        <table class="min-w-full text-left">
          <thead>
            <tr class="text-xs font-medium uppercase tracking-wider text-[#42A9C2]">
              <th class="px-6 py-3 whitespace-nowrap">{{ $t('liquidation.unchanged_records.excel_line') }}</th>
              <th class="px-6 py-3">{{ $t('liquidation.unchanged_records.cups') }}</th>
              <th class="px-6 py-3">{{ $t('liquidation.unchanged_records.result') }}</th>
            </tr>
          </thead>
          <tbody class="text-sm text-gray-600">
            <tr
              v-for="(row, index) in normalizedAlreadyUpdatedRows"
              :key="`${row.cups || 'already'}-${index}`"
              class="border-t border-gray-100 align-top hover:bg-gray-50"
            >
              <td class="px-6 py-3 whitespace-nowrap text-gray-500">{{ row.excelLine ?? '-' }}</td>
              <td class="px-6 py-3 whitespace-nowrap font-medium text-[#385660]">{{ row.cups || '-' }}</td>
              <td class="px-6 py-3 text-gray-500">
                {{ $t('liquidation.unchanged_records.result_unchanged', { cups: row.cups || '-' }) }}
                <p v-if="row.warnings.length" class="mt-2 text-xs text-[#fd7e14]">{{ row.warnings.join(' ') }}</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>

  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="showUpdateConfirmModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
      >
        <div class="w-full max-w-md rounded-xl bg-white p-6 shadow-card border flex flex-col gap-4">
          <h3 class="text-lg font-semibold text-center">{{ $t('liquidation.upload.confirm_title') }}</h3>
          <p class="text-sm md:text-base text-center text-gray-600">
            {{ $t('liquidation.upload.confirm_text') }}
          </p>
          <div class="flex items-center justify-center gap-3 pt-2">
            <button
              class="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition cursor-pointer"
              @click="closeUpdateConfirmModal"
            >
              {{ $t('liquidation.upload.confirm_cancel') }}
            </button>
            <button
              class="px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition cursor-pointer"
              @click="confirmUploadWithUpdate"
            >
              {{ $t('liquidation.upload.confirm_accept') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import { watch } from 'vue';
import { useExportStore } from '@/stores/exportStore';

import SearchPrimary from '@/components/search/search-primary.vue';
import TableComponent from '@/components/table/TableComponent.vue';
import PaginateComponent from '@/components/paginate/PaginateComponent.vue';
import ButtonWhite from '@/components/button/ButtonWhite.vue';
import ExportFetch from '@/components/fetch/exportFetch.vue';
import PrintTableComponent from '@/components/print/PrintTableComponent.vue';

import ArrowReload from '@/assets/svg/ArrowReload.vue';
import PrintIcon from '@/assets/svg/PrintIcon.vue';
import SearchIcon from '@/assets/svg/SearchIcon.vue';
import TitlePrimary from '@/components/title/title-primary.vue';
import check from '@/assets/svg/check.vue';

const { t, te } = useI18n();
const exportStore = useExportStore();
const propertyType = ref('comunidad');

// --- Estado ---
const items = ref([]);
const currentPage = ref(1);
const totalPages = ref(1);
const loading = ref(true);

// --- Funciones de exportación ---
const handleExportStarted = () => {
  console.log(t('liquidation.export.started'));
  exportStore.startExport('Exportando liquidaciones...');
};

const handleExportCompleted = () => {
  console.log(t('liquidation.export.completed'));
  exportStore.completeExport();
};

const handleExportFailed = () => {
  console.log(t('liquidation.export.failed'));
  exportStore.failExport('Error al exportar liquidaciones');
};

// --- Funciones de impresión ---
const handlePrintStarted = () => {
  console.log(t('liquidation.print.started'));
};

const handlePrintCompleted = () => {
  console.log(t('liquidation.print.completed'));
};

const handlePrintFailed = (error) => {
  console.error(t('liquidation.print.failed'), error);
};

// --- Lógica de datos ---
const fetchDataFromApi = async () => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}/supply/user/list/light`,
      {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        params: {
          per_page: 10,
          page: currentPage.value
        }
      }
    );

    if (response.data?.value.data) {
      items.value = response.data.value.data;
      totalPages.value = response.data.value.last_page;
    }
  } catch (error) {
    items.value = [];
    console.error(t('errors.fetch'), error);
  }
};

watch(currentPage, (newPage) => {
  fetchDataFromApi();
});

const handlePageChange = (newPage) => {
  currentPage.value = newPage;
  fetchDataFromApi();
};

onMounted(() => {
  fetchDataFromApi();
});

// file upload state
const fileInput = ref(null);
const selectedFile = ref(null);
const uploading = ref(false);
const updateIfExists = ref(false);
const showUpdateConfirmModal = ref(false);
const uploadMessage = ref('');
const uploadMessageType = ref('success');
const notUpdatedFields = ref([]);
const updatedRows = ref([]);
const alreadyUpdatedRows = ref([]);
// Totales de la última carga (ADS-0085).
const uploadSummary = ref(null);
// Color del mensaje de la caja de subida y del aviso del resultado según su tono (ADS-0085).
const uploadMessageClasses = { success: 'text-green-600', warning: 'text-[#fd7e14]', error: 'text-red-600' };
const summaryNoticeClasses = {
  success: 'bg-[#eafaff] border-[#2AB4D9]',
  warning: 'bg-[#fff4e6] border-[#fd7e14]',
  error: 'bg-[#fdecee] border-[#dc3545]',
};

// Las etiquetas de los campos salen de los idiomas (ADS-0085): las que faltaban
// se mostraban en inglés («Address», «Contract expiration»).
const toFriendlyLabel = (field) => {
  if (te(`liquidation.fields.${field}`)) {
    return t(`liquidation.fields.${field}`);
  }

  const normalized = String(field || '')
    .replace(/_/g, ' ')
    .trim();

  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const formatExcelDate = (rawValue) => {
  const serial = Number(rawValue);

  if (!Number.isFinite(serial) || serial <= 0) {
    return String(rawValue);
  }

  const utcDate = new Date(Math.round((serial - 25569) * 86400 * 1000));

  if (Number.isNaN(utcDate.getTime())) {
    return String(rawValue);
  }

  return utcDate.toLocaleDateString('es-ES');
};

const formatFieldValue = (field, value) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  if (field === 'fecha') {
    return formatExcelDate(value);
  }

  return String(value);
};

const normalizeExcelLine = (log) => {
  const candidates = [
    log?.excel_row,
    log?.excel_line,
    log?.row_number,
    log?.line_number,
    log?.row,
    log?.line,
  ];

  for (const candidate of candidates) {
    const parsed = Number(candidate);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return null;
};

const normalizedNotUpdatedFields = computed(() => {
  return notUpdatedFields.value.map((log) => ({
    ...log,
    excel_line: normalizeExcelLine(log),
    // La API agrupa por CUPS y devuelve todos sus motivos; un archivo antiguo
    // en caché puede traer todavía un único `reason` (ADS-0120).
    reasons: Array.isArray(log?.reasons) && log.reasons.length
      ? log.reasons
      : [log?.reason].filter(Boolean),
  }));
});

const buildChangeEntries = (row) => {
  const changedFields = row?.details?.changed_fields;

  if (!changedFields || typeof changedFields !== 'object') {
    return [];
  }

  const entries = new Map();

  Object.values(changedFields).forEach((group) => {
    Object.entries(group || {}).forEach(([field, change]) => {
      entries.set(field, {
        field,
        label: toFriendlyLabel(field),
        from: formatFieldValue(field, change?.from),
        to: formatFieldValue(field, change?.to),
      });
    });
  });

  return Array.from(entries.values());
};

const normalizedUpdatedRows = computed(() => {
  return updatedRows.value.map((row) => {
    const isCreated = row?.details?.supply === 'created';
    const changes = buildChangeEntries(row);

    return {
      cups: row?.cups || '',
      excelLine: normalizeExcelLine(row),
      // La cliente lee el resultado por CUPS, no por número de línea (ADS-0120).
      result: t(isCreated ? 'liquidation.updated_records.result_created' : 'liquidation.updated_records.result_updated',
        { cups: row?.cups || '-' }),
      changes,
      warnings: Array.isArray(row?.warnings) ? row.warnings : [],
    };
  });
});

// Tarjetas de cifras del resultado, con los colores de estado de la app (ADS-0085).
const summaryTiles = computed(() => {
  const summary = uploadSummary.value;
  if (!summary) {
    return [];
  }

  return [
    { key: 'total', label: t('liquidation.result.total'), value: summary.total, dot: 'bg-[#2AB4D9]', text: 'text-[#385660]' },
    { key: 'updated', label: t('liquidation.result.updated'), value: summary.updated, dot: 'bg-[#28a745]', text: 'text-[#28a745]' },
    { key: 'unchanged', label: t('liquidation.result.unchanged'), value: summary.unchanged, dot: 'bg-[#7CADB9]', text: 'text-[#385660]' },
    { key: 'failed', label: t('liquidation.result.failed'), value: summary.failed, dot: 'bg-[#dc3545]', text: 'text-[#dc3545]' },
  ];
});

/**
 * Aviso de una carga que no actualiza nada (ADS-0085). Repite las cifras de las
 * tarjetas: solo dice que todo estaba actualizado si no hubo errores.
 */
const summaryNotice = computed(() => {
  const summary = uploadSummary.value;
  if (!summary || summary.updated > 0) {
    return null;
  }

  if (summary.total === 0) {
    return { type: 'error', text: t('liquidation.upload.empty') };
  }

  if (summary.failed === 0) {
    return { type: 'success', text: t('liquidation.upload.nothing_new') };
  }

  if (summary.unchanged === 0) {
    return { type: 'error', text: t('liquidation.upload.all_failed', summary.failed) };
  }

  return {
    type: 'warning',
    text: t('liquidation.upload.unchanged_and_failed', {
      unchanged: t('liquidation.upload.unchanged_count', summary.unchanged),
      failed: t('liquidation.upload.failed_count', summary.failed),
    }),
  };
});

const normalizedAlreadyUpdatedRows = computed(() => {
  return alreadyUpdatedRows.value.map((row) => ({
    cups: row?.cups || '',
    excelLine: normalizeExcelLine(row),
    warnings: Array.isArray(row?.warnings) ? row.warnings : [],
  }));
});

const getBackendTranslateKey = (payload) => {
  return payload?.value?.translate || payload?.translate || '';
};

const setUploadMessage = (messageKey, fallbackKey, type) => {
  const keyToUse = messageKey || fallbackKey;
  uploadMessage.value = te(keyToUse) ? t(keyToUse) : keyToUse;
  uploadMessageType.value = type;
};

/**
 * Resumen de la carga con los tres grupos que se revisan después: lo cargado o
 * actualizado, lo que ya estaba igual y lo que no se pudo procesar (ADS-0120).
 */
const setUploadSuccessMessage = (messageKey, fallbackKey, counts) => {
  uploadSummary.value = {
    total: counts.loaded + counts.unchanged + counts.failed,
    updated: counts.loaded,
    unchanged: counts.unchanged,
    failed: counts.failed,
  };
  uploadMessageType.value = 'success';

  // Sin nada actualizado no se dice «actualizados con éxito»: la caja repite el
  // aviso del resultado, con su color (ADS-0085).
  if (summaryNotice.value) {
    uploadMessage.value = summaryNotice.value.text;
    uploadMessageType.value = summaryNotice.value.type;
    return;
  }

  // Las cifras van en las tarjetas del resultado; el mensaje queda corto.
  const keyToUse = messageKey || fallbackKey;
  uploadMessage.value = te(keyToUse) ? t(keyToUse) : keyToUse;
};

const triggerFileUpload = () => {
  fileInput.value?.click();
};

const handleFileChange = (event) => {
  const file = event.target.files && event.target.files[0];
  if (file) {
    selectedFile.value = file;
    uploadMessage.value = '';
    uploadSummary.value = null;
    notUpdatedFields.value = [];
    updatedRows.value = [];
    alreadyUpdatedRows.value = [];
  }
  // reset input so same file can be chosen again if needed
  event.target.value = '';
};

const executeUploadContracts = async () => {
  if (!selectedFile.value) {
    setUploadMessage('', 'liquidation.upload.no_file', 'error');
    return;
  }

  uploading.value = true;
  const formData = new FormData();
  formData.append('file', selectedFile.value);
  formData.append('update_if_exists', updateIfExists.value ? '1' : '0');
  formData.append('property_type', propertyType.value);

  try {
    const response = await axios.post(
      `${import.meta.env.VITE_API_URL}/property/user/import`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );

    console.log('Import response', response);
    const backendTranslateKey = getBackendTranslateKey(response.data);
    notUpdatedFields.value = Array.isArray(response.data?.not_updated_fields)
      ? response.data.not_updated_fields
      : [];
    updatedRows.value = Array.isArray(response.data?.updated_rows)
      ? response.data.updated_rows
      : [];
    alreadyUpdatedRows.value = Array.isArray(response.data?.already_updated_rows)
      ? response.data.already_updated_rows
      : [];
    setUploadSuccessMessage(backendTranslateKey, 'liquidation.upload.success', {
      loaded: updatedRows.value.length,
      unchanged: alreadyUpdatedRows.value.length,
      failed: notUpdatedFields.value.length,
    });
    // reset selection if desired
    selectedFile.value = null;
  } catch (error) {
    console.error('Error al subir archivo', error);
    const backendTranslateKey = getBackendTranslateKey(error?.response?.data);
    setUploadMessage(backendTranslateKey, 'liquidation.upload.failure', 'error');
    uploadSummary.value = null;
    notUpdatedFields.value = Array.isArray(error?.response?.data?.not_updated_fields)
      ? error.response.data.not_updated_fields
      : [];
    updatedRows.value = Array.isArray(error?.response?.data?.updated_rows)
      ? error.response.data.updated_rows
      : [];
    alreadyUpdatedRows.value = Array.isArray(error?.response?.data?.already_updated_rows)
      ? error.response.data.already_updated_rows
      : [];
  } finally {
    uploading.value = false;
  }
};

const uploadContracts = async () => {
  if (!selectedFile.value) {
    setUploadMessage('', 'liquidation.upload.no_file', 'error');
    return;
  }

  if (updateIfExists.value) {
    showUpdateConfirmModal.value = true;
    return;
  }

  await executeUploadContracts();
};

const closeUpdateConfirmModal = () => {
  showUpdateConfirmModal.value = false;
};

const confirmUploadWithUpdate = async () => {
  showUpdateConfirmModal.value = false;
  await executeUploadContracts();
};

const handleAdd = (item) => {
  console.log(t('actions.add'), item);
};
</script>

<style scoped>
/* Tus estilos actuales se mantienen igual */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.check {
  cursor: pointer;
  position: relative;
  margin: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 18px;
  width: 18px;
  height: 18px;
  -webkit-tap-highlight-color: transparent;
  transform: translate3d(0, 0, 0);
}

.check:before {
  content: "";
  position: absolute;
  top: -15px;
  left: -15px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(34,50,84,0.03);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.check svg {
  position: relative;
  z-index: 1;
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke: #c8ccd4;
  stroke-width: 1.5;
  transform: translate3d(0, 0, 0);
  transition: all 0.2s ease;
}

.check svg path {
  stroke-dasharray: 60;
  stroke-dashoffset: 0;
}

.check svg polyline {
  stroke-dasharray: 22;
  stroke-dashoffset: 66;
}

.check:hover:before {
  opacity: 1;
}

.check:hover svg {
  stroke: #4285f4;
}

#cbx:checked + .check svg {
  stroke: #4285f4;
}

#cbx:checked + .check svg path {
  stroke-dashoffset: 60;
  transition: all 0.3s linear;
}

#cbx:checked + .check svg polyline {
  stroke-dashoffset: 42;
  transition: all 0.2s linear;
  transition-delay: 0.15s;
}
</style>
